import React, { useEffect, useState } from "react";
import { api, extractError } from "../lib/api";
import { formatKg, formatWeightKg, kgToQtl, qtlToKg } from "../lib/weightUnits";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";

/**
 * Correct a recorded outward tare or gross. Net is recalculated.
 * A finalized sales settlement is reopened for accounts review.
 */
export default function OutwardWeighCorrection({ dispatch, onSaved }) {
  const hasTare = dispatch?.tare_weight_qtl != null;
  const hasGross = dispatch?.gross_weight_qtl != null;
  const finalized = dispatch?.state === "COMPLETED" || dispatch?.sales_settlement_status === "FINAL";
  const [kind, setKind] = useState(hasGross ? "gross" : "tare");
  const [weight, setWeight] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const qtl = kind === "gross" ? dispatch?.gross_weight_qtl : dispatch?.tare_weight_qtl;
    const kg = qtlToKg(qtl);
    setWeight(kg != null ? String(kg) : "");
  }, [kind, dispatch?.id, dispatch?.tare_weight_qtl, dispatch?.gross_weight_qtl]);

  const submit = async (e) => {
    e.preventDefault();
    const wKg = parseFloat(weight);
    if (!Number.isFinite(wKg) || wKg <= 0) {
      toast.error("Enter a valid weight in kg");
      return;
    }
    const wQtl = kgToQtl(wKg);
    if (wQtl == null) {
      toast.error("Enter a valid weight in kg");
      return;
    }
    try {
      setSaving(true);
      await api.patch(`/outward-dispatches/${dispatch.id}/weigh-correction`, {
        kind,
        weight_qtl: wQtl,
        note: note.trim() || undefined,
      });
      toast.success(`${kind === "gross" ? "Gross" : "Tare"} corrected (${formatKg(wKg)} kg)`);
      setNote("");
      onSaved?.();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!dispatch || (!hasTare && !hasGross)) {
    return null;
  }

  return (
    <div className="rounded-md border border-border bg-card text-foreground p-4" data-testid="outward-weigh-correction">
      <p className="text-xs font-medium">Correct outward weighbridge weights</p>
      <p className="text-[10px] text-muted-foreground mt-1 leading-relaxed">
        Fix tare or gross if the wrong reading was recorded (enter kg). Net is recalculated from gross minus tare.
      </p>
      <dl className="grid grid-cols-3 gap-2 mt-3 text-[10px] tabular">
        <div>
          <dt className="text-muted-foreground uppercase">Tare</dt>
          <dd className="font-medium">{formatWeightKg(dispatch?.tare_weight_qtl) ?? "—"}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground uppercase">Gross</dt>
          <dd className="font-medium">{formatWeightKg(dispatch?.gross_weight_qtl) ?? "—"}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground uppercase">Net</dt>
          <dd className="font-medium">{formatWeightKg(dispatch?.net_weight_qtl) ?? "—"}</dd>
        </div>
      </dl>
      {finalized ? (
        <p className="text-xs text-amber-800 dark:text-amber-300 mt-3">
          This dispatch was finalized. Saving a correction moves it back to accounts pending for re-review.
        </p>
      ) : null}
      <form onSubmit={submit} className="mt-3 grid gap-3 grid-cols-1 sm:grid-cols-2">
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">Weight type</Label>
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tare" disabled={!hasTare}>Tare (empty truck)</SelectItem>
              <SelectItem value="gross" disabled={!hasGross}>Gross (loaded truck)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">Bridge reading (kg)</Label>
          <Input
            type="number"
            step="1"
            min="0"
            className="mt-1 h-9"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            data-testid="outward-weigh-correction-input"
          />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-[10px] uppercase text-muted-foreground">Reason (optional)</Label>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className="mt-1 text-xs"
            placeholder="e.g. scale misread, re-weighed at bridge…"
          />
        </div>
        <div className="sm:col-span-2 flex justify-end">
          <Button
            type="submit"
            disabled={saving}
            variant="outline"
            className="min-h-9 border-border"
            data-testid="outward-weigh-correction-submit"
          >
            {saving ? "Saving…" : `Save ${kind} correction`}
          </Button>
        </div>
      </form>
    </div>
  );
}
